import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App as AntApp, Input, Segmented } from 'antd';
import SheetSelect from '../components/SheetSelect';
import AppBar from '../components/AppBar';
import { AsyncButton } from '../components/kit';
import * as contactsApi from '../api/contacts';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import { NATIONALITY_OPTIONS, isMalaysian } from '../lib/nationalities';

// One form, two modes: /contacts/:type/new (create) and /contacts/:type/:id/edit (edit).
// Fields are gated per contact type — vendors/purchasers carry entity_type + nationality;
// co-agencies carry a company board-registration no + email. registration_no is required
// everywhere (NOT NULL in the DB). Bank accounts are fully replaced on save.
const CFG = {
  vendor: {
    noun: 'vendor', getFn: contactsApi.getVendorsById, createFn: contactsApi.createVendors, updateFn: contactsApi.updateVendorsById,
    subTypes: [{ label: 'Vendor', value: 'VENDOR' }, { label: 'Landlord', value: 'LANDLORD' }],
    hasEntity: true, hasNationality: true, hasEmail: false, hasBoardReg: false, defaultEntity: 'COMPANY',
  },
  purchaser: {
    noun: 'purchaser', getFn: contactsApi.getPurchasersById, createFn: contactsApi.createPurchasers, updateFn: contactsApi.updatePurchasersById,
    subTypes: [{ label: 'Purchaser', value: 'PURCHASER' }, { label: 'Tenant', value: 'TENANT' }],
    hasEntity: true, hasNationality: true, hasEmail: false, hasBoardReg: false, defaultEntity: 'COMPANY',
  },
  coagency: {
    noun: 'co-agency', getFn: contactsApi.getCoAgenciesById, createFn: contactsApi.createCoAgencies, updateFn: contactsApi.updateCoAgenciesById,
    subTypes: [{ label: 'Co-agency', value: 'CO_AGENCY' }, { label: 'Referral', value: 'REFERRAL' }],
    hasEntity: false, hasNationality: false, hasEmail: true, hasBoardReg: true, defaultEntity: 'COMPANY',
  },
};

const isCompany = (e) => !e || e === 'COMPANY';
const regLabel = (entity, nat) => (isCompany(entity) ? 'SSM Registration No' : isMalaysian(nat) ? 'IC Number' : 'Passport Number');
const regHint = (entity, nat) => (isCompany(entity) ? 'e.g. 201901012345' : isMalaysian(nat) ? 'e.g. 900101-14-5678' : 'Passport number');

const emptyBank = () => ({ bank: '', acct: '', name: '', is_primary: false });

export default function ContactFormPage() {
  const nav = useNavigate();
  const { type, id } = useParams();
  const { message } = AntApp.useApp();
  const cfg = CFG[type] || CFG.vendor;
  const editing = !!id;

  const [loaded, setLoaded] = useState(!editing);
  const [subType, setSubType] = useState(cfg.subTypes[0].value);
  const [entity, setEntity] = useState(cfg.defaultEntity);
  const [nationality, setNationality] = useState('Malaysian');
  const [name, setName] = useState('');
  const [registrationNo, setRegistrationNo] = useState('');
  const [boardRegNo, setBoardRegNo] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [address, setAddress] = useState('');
  const [banks, setBanks] = useState([]);
  const [banksLocked, setBanksLocked] = useState(null); // creator's name when the banks are not this agent's to change
  // Unsaved typing. Raised by the agent's own edits only (the `edit` / `onText`
  // wrappers below) — the edit-mode load writes through the raw setters, so
  // opening a contact never counts as a change.
  const [dirty, setDirty] = useState(false);
  const edit = (set) => (v) => { setDirty(true); set(v); };
  const onText = (set) => (e) => { setDirty(true); set(e.target.value); };

  useEffect(() => {
    if (!editing) return;
    cfg.getFn(id)
      .then((r) => {
        const d = r?.data ?? r;
        setName(d.name || '');
        setSubType(d.sub_type || cfg.subTypes[0].value);
        setEntity(d.entity_type || cfg.defaultEntity);
        setNationality(d.nationality || 'Malaysian');
        setRegistrationNo(d.registration_no || '');
        setBoardRegNo(d.board_register_no || '');
        setPhone(d.phone || '');
        setEmail(d.email || '');
        setContactPerson(d.contact_person || '');
        setAddress(d.address || '');
        setBanks((d.bank_accounts || []).map((b) => ({ bank: b.bank || '', acct: b.acct || '', name: b.name || '', is_primary: !!b.is_primary })));
        // Someone else's landlord / tenant: the details are yours to fix, the bank
        // accounts are not (the server refuses a change to them).
        setBanksLocked(d.can_edit_banks === false ? (d.creator_name || 'HQ') : null);
        setLoaded(true);
      })
      .catch((e) => message.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  const editBanks = edit(setBanks);
  const addBank = () => editBanks((xs) => [...xs, { ...emptyBank(), is_primary: xs.length === 0 }]);
  const patchBank = (i, patch) => editBanks((xs) => xs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const removeBank = (i) => editBanks((xs) => xs.filter((_, j) => j !== i));
  const setPrimary = (i) => editBanks((xs) => xs.map((b, j) => ({ ...b, is_primary: j === i })));

  // Validates and writes, without moving: the Save button navigates to the saved
  // contact afterwards, the leave guard's "Save & leave" goes wherever the agent
  // was heading instead. Resolves to the contact's id, or null when a check
  // stopped it (the warning has already said why).
  const persist = async () => {
    if (!name.trim()) { message.warning('Name is required'); return null; }
    if (!registrationNo.trim()) { message.warning(`${regLabel(entity, nationality)} is required`); return null; }

    const partialBank = banks.some((b) => {
      const filled = [b.bank, b.acct, b.name].filter((v) => v.trim()).length;
      return filled > 0 && filled < 3;
    });
    if (partialBank) { message.warning('Complete or remove the partial bank account'); return null; }

    const payload = {
      name: name.trim(),
      sub_type: subType,
      registration_no: registrationNo.trim(),
      phone: phone.trim(),
      contact_person: contactPerson.trim(),
      address: address.trim(),
      ...(cfg.hasEntity ? { entity_type: entity } : {}),
      ...(cfg.hasNationality && entity === 'INDIVIDUAL' ? { nationality } : {}),
      ...(cfg.hasEmail ? { email: email.trim() } : {}),
      ...(cfg.hasBoardReg ? { board_register_no: boardRegNo.trim() } : {}),
      ...(banksLocked ? {} : { bank_accounts: banks.filter((b) => b.bank.trim() || b.acct.trim() || b.name.trim()) }),
    };

    if (editing) {
      await cfg.updateFn(id, payload);
      message.success('Contact updated');
      return id;
    }
    const r = await cfg.createFn(payload);
    const created = r?.data ?? r;
    message.success('Contact created');
    // A reply without an id still means saved — '' rather than null, and the Save
    // button falls back to the list.
    return created?.id || '';
  };

  const guard = useUnsavedChanges(dirty, {
    what: 'this contact',
    saveLabel: 'Save & leave',
    onSave: async () => {
      try {
        return (await persist()) !== null;
      } catch (e) {
        message.error(e.message);
        return false;
      }
    },
  });

  const save = async () => {
    const savedId = await persist();
    if (savedId === null) return;
    // Saved: let the move to the contact through the leave guard.
    setDirty(false);
    guard.release();
    nav(savedId ? `/contacts/${type}/${savedId}` : '/contacts');
  };

  const individual = cfg.hasEntity && entity === 'INDIVIDUAL';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title={editing ? 'Edit contact' : `New ${cfg.noun}`} sub={editing ? cfg.noun : 'reusable across deals & forms'} />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!loaded ? (
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            {/* Lead card = what kind of contact this is, as on the letter form. */}
            <div style={card}>
              <Field label="Role" req>
                <Segmented block options={cfg.subTypes} value={subType} onChange={edit(setSubType)} />
              </Field>
              {cfg.hasEntity && (
                <Field label="Company / Individual" req>
                  <Segmented block options={[{ label: 'Company', value: 'COMPANY' }, { label: 'Individual', value: 'INDIVIDUAL' }]} value={entity} onChange={edit(setEntity)} />
                </Field>
              )}
            </div>

            <div style={sectionLabel}>IDENTITY</div>
            <div style={card}>
              <Field label={individual ? 'Full name (as per IC)' : 'Name (as per SSM)'} req>
                <Input value={name} onChange={onText(setName)} placeholder={individual ? 'Full legal name' : 'Company / agency name'} />
              </Field>

              {individual && (
                <Field label="Nationality" req>
                  <SheetSelect title="Nationality" value={nationality} onChange={edit(setNationality)} options={NATIONALITY_OPTIONS} showSearch searchPlaceholder="Search nationality…" />
                </Field>
              )}

              <Field label={regLabel(entity, nationality)} req>
                <Input value={registrationNo} onChange={onText(setRegistrationNo)} placeholder={regHint(entity, nationality)} />
              </Field>

              {cfg.hasBoardReg && (
                <Field label="Board registration no">
                  <Input value={boardRegNo} onChange={onText(setBoardRegNo)} placeholder="e.g. E(1)1234" />
                </Field>
              )}
            </div>

            <div style={sectionLabel}>CONTACT</div>
            <div style={card}>
              <Field label="Contact person">
                <Input value={contactPerson} onChange={onText(setContactPerson)} placeholder="Person in charge" />
              </Field>
              {/* Same registry column as the quick-create sheet — same label. */}
              <Field label="Contact No">
                <Input value={phone} onChange={onText(setPhone)} placeholder="+60 12-345 6789" />
              </Field>
              {cfg.hasEmail && (
                <Field label="Email">
                  <Input type="email" value={email} onChange={onText(setEmail)} placeholder="name@email.com" />
                </Field>
              )}
              <Field label="Address">
                <Input.TextArea value={address} onChange={onText(setAddress)} rows={2} placeholder="Mailing address" />
              </Field>
            </div>

            {banksLocked ? (
              <>
                <div style={sectionLabel}>BANK ACCOUNTS</div>
                <div style={{ ...card, fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
                  {banks.length ? banks.map((b, i) => <div key={i} style={{ color: 'var(--text)' }}>{b.bank} · {b.acct}{b.is_primary ? ' · primary' : ''}</div>) : null}
                  <div>Registered by {banksLocked} — only HQ or that agent can change its bank accounts.</div>
                </div>
              </>
            ) : (<>
            <div style={{ ...sectionLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>BANK ACCOUNTS</span>
              {/* letterSpacing reset: the section label tracks wide, the action should not */}
              <span onClick={addBank} style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 12.5, fontWeight: 600, letterSpacing: 0 }}>＋ Add</span>
            </div>
            {banks.length === 0 ? (
              <div style={{ ...card, fontSize: 13, color: 'var(--muted)' }}>
                No bank accounts yet. Tap ＋ Add to include one.
              </div>
            ) : banks.map((b, i) => (
              <div key={i} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span onClick={() => setPrimary(i)} style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: b.is_primary ? 'var(--primary)' : 'var(--muted)' }}>
                    {b.is_primary ? '★ Primary' : '☆ Set primary'}
                  </span>
                  <span onClick={() => removeBank(i)} style={{ cursor: 'pointer', color: 'var(--bad)', fontSize: 12.5, fontWeight: 600 }}>Remove</span>
                </div>
                <Field label="Bank">
                  <Input value={b.bank} onChange={(e) => patchBank(i, { bank: e.target.value })} placeholder="e.g. Maybank" />
                </Field>
                <Field label="Account number">
                  <Input value={b.acct} onChange={(e) => patchBank(i, { acct: e.target.value })} placeholder="e.g. 1234567890" />
                </Field>
                <Field label="Account holder name">
                  <Input value={b.name} onChange={(e) => patchBank(i, { name: e.target.value })} placeholder="Name as per bank account" />
                </Field>
              </div>
            ))}
            </>)}
          </>
        )}
      </div>

      <div style={{ padding: '12px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none' }}>
        <AsyncButton type="primary" block onClick={save} onError={(e) => message.error(e.message)}>{editing ? 'Save changes' : 'Create'}</AsyncButton>
      </div>
    </div>
  );
}

// Shared form furniture — same tokens as LetterCreatePage / SaleCreatePage / ListingFormPage,
// so every long form in the app reads as one family.
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 };
const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', marginBottom: -6, marginTop: 2 };
// `req` renders the admin-portal-style red asterisk on mandatory fields.
const Field = ({ label, req, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
      {req && <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>}{label}
    </label>
    {children}
  </div>
);
